import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CopyableValue } from "@/components/CopyableValue";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  api,
  type AgentKeyCapabilityCategory,
  type AgentKeyRow,
} from "@/lib/api";
import { formatDate, relativeIso } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { AlertTriangle, KeyRound, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const KEYS_QUERY = ["admin-agent-keys"] as const;
const CAPS_QUERY = ["admin-agent-key-capabilities"] as const;

// Keys that can read runtime feedback reports (customer conversations) must
// expire within 90 days; the backend refuses an open-ended one.
const FEEDBACK_READ = "feedback:read";

type ExpiryChoice = "never" | "7" | "30" | "90";
const EXPIRY_OPTIONS: Array<{ value: ExpiryChoice; label: string }> = [
  { value: "never", label: "Never" },
  { value: "7", label: "In 7 days" },
  { value: "30", label: "In 30 days" },
  { value: "90", label: "In 90 days" },
];

function expiresAtIso(choice: ExpiryChoice): string | null {
  if (choice === "never") return null;
  return new Date(Date.now() + Number(choice) * 24 * 60 * 60 * 1000).toISOString();
}

function totalCaps(categories: AgentKeyCapabilityCategory[]): number {
  return categories.reduce((n, c) => n + c.capabilities.length, 0);
}

// Immutable set toggles for capability selection, shared by the create and edit
// sheets.
function withCapability(set: Set<string>, id: string, on: boolean): Set<string> {
  const next = new Set(set);
  if (on) next.add(id);
  else next.delete(id);
  return next;
}
function withGroup(set: Set<string>, cat: AgentKeyCapabilityCategory, on: boolean): Set<string> {
  const next = new Set(set);
  for (const cap of cat.capabilities) {
    if (on) next.add(cap.id);
    else next.delete(cap.id);
  }
  return next;
}

export function AdminAgentKeysPage() {
  useDocumentTitle("Admin: Agent keys");
  const keysQuery = useQuery({ queryKey: KEYS_QUERY, queryFn: api.admin.agentKeys });
  const capsQuery = useQuery({ queryKey: CAPS_QUERY, queryFn: api.admin.agentKeyCapabilities });

  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AgentKeyRow | null>(null);
  const [detailTarget, setDetailTarget] = useState<AgentKeyRow | null>(null);
  // The raw token (shown exactly once, held only in memory, never persisted)
  // plus the copyable usage guide for the new key.
  const [created, setCreated] = useState<{ token: string; usageGuide: string } | null>(null);

  const permissions = keysQuery.data?._permissions;
  // Fail closed while loading: default to hidden until the server confirms the
  // permission, so create/revoke controls don't flash before _permissions land.
  const canCreate = permissions?.canCreate === true;
  const canRevoke = permissions?.canRevoke === true;
  const canEdit = permissions?.canEdit === true;

  const categories = useMemo(
    () => capsQuery.data?.data.categories ?? [],
    [capsQuery.data],
  );
  // capability id → human label, for rendering key rows.
  const capLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const cat of categories) for (const cap of cat.capabilities) m.set(cap.id, cap.label);
    return m;
  }, [categories]);

  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: (id: string) => api.admin.revokeAgentKey(id),
    onSuccess: () => {
      toast.success("Key revoked");
      queryClient.invalidateQueries({ queryKey: KEYS_QUERY });
      setRevokeTarget(null);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to revoke key"),
  });

  const columns: ColumnDef<AgentKeyRow>[] = [
    {
      accessorKey: "label",
      header: "Label",
      cell: ({ row }) => <span className="font-medium">{row.original.label}</span>,
    },
    {
      id: "capabilities",
      header: "Permissions",
      cell: ({ row }) => {
        const caps = row.original.capabilities;
        if (caps.length === 0) return <span className="text-muted-foreground">None</span>;
        const shown = caps.slice(0, 3);
        return (
          <div className="flex flex-wrap items-center gap-1">
            {shown.map((id) => (
              <Badge key={id} variant="secondary" className="font-normal">
                {capLabels.get(id) ?? id}
              </Badge>
            ))}
            {caps.length > shown.length && (
              <span className="text-xs text-muted-foreground">+{caps.length - shown.length} more</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.createdAt)}</span>,
    },
    {
      accessorKey: "lastUsedAt",
      header: "Last used",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.lastUsedAt ? relativeIso(row.original.lastUsedAt) : "—"}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) =>
        row.original.revokedAt ? (
          <Badge variant="destructive">Revoked</Badge>
        ) : (
          <Badge>Active</Badge>
        ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        !row.original.revokedAt && canRevoke ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setRevokeTarget(row.original);
            }}
          >
            Revoke
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="API keys"
          subtitle="Long-lived keys for external automation. Each key can act across organizations, but only for the permissions you grant it."
        />
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Generate key
          </Button>
        )}
      </div>

      {keysQuery.isLoading ? (
        <LoadingIndicator />
      ) : keysQuery.isError || !keysQuery.data ? (
        <ErrorComponent error={keysQuery.error ?? "Failed to load keys"} />
      ) : keysQuery.data.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No keys yet. Generate one to get started.</p>
      ) : (
        <DataTable columns={columns} data={keysQuery.data.data} onRowClick={setDetailTarget} />
      )}

      <CreateKeySheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        categories={categories}
        onCreated={(result) => {
          setCreateOpen(false);
          setCreated(result);
          queryClient.invalidateQueries({ queryKey: KEYS_QUERY });
        }}
      />

      <KeyDetailSheet
        key={detailTarget?.id ?? "none"}
        keyRow={detailTarget}
        categories={categories}
        onClose={() => setDetailTarget(null)}
        canRevoke={canRevoke}
        canEdit={canEdit}
        onRevoke={(k) => {
          setDetailTarget(null);
          setRevokeTarget(k);
        }}
      />

      <TokenRevealDialog created={created} onClose={() => setCreated(null)} />

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title="Revoke key?"
        description={
          revokeTarget
            ? `"${revokeTarget.label}" will stop working immediately. This cannot be undone.`
            : ""
        }
        confirmLabel="Revoke"
        variant="destructive"
        loading={revoke.isPending}
        onConfirm={() => revokeTarget && revoke.mutate(revokeTarget.id)}
      />
    </div>
  );
}

// One capability row: label + description on the left, a toggle on the right.
// `readOnly` renders a disabled toggle for the detail (view) mode.
function CapabilityRow({
  label,
  description,
  on,
  onToggle,
  readOnly,
}: {
  label: string;
  description?: string;
  on: boolean;
  onToggle?: (on: boolean) => void;
  readOnly?: boolean;
}) {
  const body = (
    <>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {description && (
          <span className="block text-xs text-muted-foreground leading-snug">{description}</span>
        )}
      </span>
      <Switch
        checked={on}
        disabled={readOnly}
        onCheckedChange={readOnly ? undefined : onToggle}
        aria-label={label}
      />
    </>
  );
  return readOnly ? (
    <div className="flex items-center justify-between gap-4 py-2.5 border-t border-border/60 first:border-t-0">
      {body}
    </div>
  ) : (
    <label className="flex items-center justify-between gap-4 py-2.5 border-t border-border/60 first:border-t-0 cursor-pointer">
      {body}
    </label>
  );
}

// Grouped permission list shared by the create and detail views. In create
// mode each group gets an "All" toggle; in read-only mode it shows an n/total
// count instead.
function PermissionGroups({
  categories,
  selected,
  readOnly,
  onToggleCap,
  onToggleGroup,
}: {
  categories: AgentKeyCapabilityCategory[];
  selected: Set<string>;
  readOnly?: boolean;
  onToggleCap?: (id: string, on: boolean) => void;
  onToggleGroup?: (cat: AgentKeyCapabilityCategory, on: boolean) => void;
}) {
  return (
    <div className="rounded-md border divide-y divide-border">
      {categories.map((cat) => {
        const n = cat.capabilities.filter((cap) => selected.has(cap.id)).length;
        const allOn = n === cat.capabilities.length;
        return (
          <div key={cat.id} className="px-3 py-2">
            <div className="flex items-center justify-between gap-4 py-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {cat.label}
              </span>
              {readOnly ? (
                <span className="text-xs text-muted-foreground">
                  {n}/{cat.capabilities.length}
                </span>
              ) : (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  All
                  <Switch
                    checked={allOn}
                    onCheckedChange={(v) => onToggleGroup?.(cat, v)}
                    aria-label={`Enable all ${cat.label}`}
                  />
                </label>
              )}
            </div>
            <div className="pl-1">
              {cat.capabilities.map((cap) => (
                <CapabilityRow
                  key={cap.id}
                  label={cap.label}
                  description={cap.description}
                  on={selected.has(cap.id)}
                  readOnly={readOnly}
                  onToggle={(v) => onToggleCap?.(cap.id, v)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CreateKeySheet({
  open,
  onOpenChange,
  categories,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: AgentKeyCapabilityCategory[];
  onCreated: (result: { token: string; usageGuide: string }) => void;
}) {
  const [label, setLabel] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expiry, setExpiry] = useState<ExpiryChoice>("never");
  const mustExpire = selected.has(FEEDBACK_READ);

  const reset = () => {
    setLabel("");
    setSelected(new Set());
    setExpiry("never");
  };

  const create = useMutation({
    mutationFn: () =>
      api.admin.createAgentKey({
        label: label.trim(),
        capabilities: [...selected],
        expiresAt: expiresAtIso(expiry),
      }),
    onSuccess: (res) => {
      onCreated({ token: res.data.key, usageGuide: res.data.usageGuide });
      reset();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to create key"),
  });

  // Switching the feedback permission on moves an open-ended key to 30 days
  // so the sheet never offers a combination the backend will refuse.
  const setCap = (id: string, on: boolean) => {
    setSelected((prev) => withCapability(prev, id, on));
    if (on && id === FEEDBACK_READ && expiry === "never") setExpiry("30");
  };
  const setGroup = (cat: AgentKeyCapabilityCategory, on: boolean) => {
    setSelected((prev) => withGroup(prev, cat, on));
    if (on && expiry === "never" && cat.capabilities.some((c) => c.id === FEEDBACK_READ)) {
      setExpiry("30");
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <SheetContent side="right" className="w-full gap-0 sm:max-w-md">
        <SheetHeader className="border-b">
          <SheetTitle>Generate API key</SheetTitle>
          <SheetDescription>
            Choose exactly what this key may do. Leave a permission off to withhold it —
            anything not enabled is denied.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="key-label">Label</Label>
            <Input
              id="key-label"
              placeholder="e.g. Ops automation"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="key-expiry">Expires</Label>
            <Select value={expiry} onValueChange={(v) => setExpiry(v as ExpiryChoice)}>
              <SelectTrigger id="key-expiry" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_OPTIONS.map((o) => (
                  <SelectItem
                    key={o.value}
                    value={o.value}
                    disabled={mustExpire && o.value === "never"}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {mustExpire && (
              <p className="text-xs text-muted-foreground">
                Keys that read runtime feedback reports must expire within 90 days.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Permissions</Label>
              <span className="text-xs text-muted-foreground">
                {selected.size} of {totalCaps(categories)} enabled
              </span>
            </div>
            <PermissionGroups
              categories={categories}
              selected={selected}
              onToggleCap={setCap}
              onToggleGroup={setGroup}
            />
          </div>
        </div>

        <SheetFooter className="flex-row justify-end gap-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!label.trim() || create.isPending}>
            Generate key
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// Read-only permission matrix for one key: the full catalog with granted
// permissions on and withheld ones off, so an operator can see exactly what a
// key can and can't do.
function KeyDetailSheet({
  keyRow,
  categories,
  onClose,
  canRevoke,
  canEdit,
  onRevoke,
}: {
  keyRow: AgentKeyRow | null;
  categories: AgentKeyCapabilityCategory[];
  onClose: () => void;
  canRevoke: boolean;
  canEdit: boolean;
  onRevoke: (k: AgentKeyRow) => void;
}) {
  const editable = canEdit && !!keyRow && !keyRow.revokedAt;
  // Working copy of the grants while editing. The parent remounts this sheet
  // per selected key (via a React `key`), so this initializer runs fresh for
  // each key — no reset-on-prop-change effect needed.
  const [draft, setDraft] = useState<Set<string>>(() => new Set(keyRow?.capabilities ?? []));

  const queryClient = useQueryClient();
  const guideKey = ["admin-agent-key-guide", keyRow?.id] as const;
  const guide = useQuery({
    queryKey: guideKey,
    queryFn: () => api.admin.agentKeyUsageGuide(keyRow!.id),
    enabled: !!keyRow,
  });

  const save = useMutation({
    mutationFn: () => api.admin.editAgentKey(keyRow!.id, [...draft]),
    onSuccess: () => {
      toast.success("Permissions updated");
      queryClient.invalidateQueries({ queryKey: KEYS_QUERY });
      // Regenerate the copyable instructions to match the new permissions.
      queryClient.invalidateQueries({ queryKey: guideKey });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to update permissions"),
  });

  if (!keyRow) return null;

  const saved = new Set(keyRow.capabilities);
  const dirty =
    editable && (draft.size !== saved.size || [...draft].some((c) => !saved.has(c)));
  // Show the working count while editing, the saved count otherwise.
  const shownCount = editable ? draft.size : keyRow.capabilities.length;

  const setCap = (id: string, on: boolean) => setDraft((prev) => withCapability(prev, id, on));
  const setGroup = (cat: AgentKeyCapabilityCategory, on: boolean) =>
    setDraft((prev) => withGroup(prev, cat, on));

  return (
    <Sheet open={keyRow !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-md">
        <SheetHeader className="border-b">
          <SheetTitle className="flex items-center gap-2">
            <KeyRound className="size-4" />
            {keyRow.label}
          </SheetTitle>
          <SheetDescription>
            Created {formatDate(keyRow.createdAt)} · Last used{" "}
            {keyRow.lastUsedAt ? relativeIso(keyRow.lastUsedAt) : "never"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{shownCount}</span> of{" "}
              {totalCaps(categories)} permissions {editable ? "enabled" : "granted"}
            </p>
            {keyRow.revokedAt ? <Badge variant="destructive">Revoked</Badge> : <Badge>Active</Badge>}
          </div>
          <PermissionGroups
            categories={categories}
            selected={editable ? draft : saved}
            readOnly={!editable}
            onToggleCap={editable ? setCap : undefined}
            onToggleGroup={editable ? setGroup : undefined}
          />

          <div className="space-y-1.5">
            <Label>Instructions for your agent</Label>
            <p className="text-xs text-muted-foreground">
              Copyable text explaining what this key can do and how to call it
              {editable ? " — updates when you save new permissions." : "."}
            </p>
            {guide.data ? (
              <CopyableValue value={guide.data.data.usageGuide} className="max-h-64 overflow-y-auto" />
            ) : (
              <Skeleton className="h-24 w-full rounded-md" />
            )}
          </div>
        </div>

        <SheetFooter className="flex-row justify-end gap-2 border-t">
          {!keyRow.revokedAt && canRevoke && (
            <Button
              variant="ghost"
              className="mr-auto text-destructive hover:text-destructive"
              onClick={() => onRevoke(keyRow)}
            >
              Revoke key
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {dirty ? "Cancel" : "Close"}
          </Button>
          {editable && (
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              Save changes
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function TokenRevealDialog({
  created,
  onClose,
}: {
  created: { token: string; usageGuide: string } | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={created !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="size-4" />
            Your new key
          </DialogTitle>
          <DialogDescription>
            Copy this now — it won't be shown again. Store it somewhere safe.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>This is the only time the full key is visible.</span>
        </div>

        {created && <CopyableValue value={created.token} />}

        {created && (
          <div className="space-y-1.5">
            <Label>Instructions for your agent</Label>
            <p className="text-xs text-muted-foreground">
              Copy this and give it to your agent — it explains what the key can do and how to
              call it. Set the key as <code>BOO_AUTOMATION_KEY</code>.
            </p>
            <CopyableValue value={created.usageGuide} className="max-h-64 overflow-y-auto" />
          </div>
        )}

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
